import { HttpStatusCode } from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SituationStatusType } from "../../../../../types/emergency";

import {
  getSituationCount,
  getSituationList,
  putSituationEnd,
  putSituationStart,
  SituationItem,
  SituationListSearchType,
} from "../../../../../services/managerEmergencyServices";

import useSimplePopup from "../../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../../hooks/useFullScreenLoading";
import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../../../hooks/useInfiniteScroll";

import StickyHeader from "../../../../../components/Layout/StickyHeader";
import { SummaryButton, SummaryButtonOption } from "../../../../Manager/Report";
import CommonButton from "../../../../../components/Button";
import CommonPopup from "../../../../../components/Popup";

import { MdAccessTimeFilled, MdLocationPin } from "react-icons/md";
import { RefreshIcon } from "../../../../../hooks/useStoreHeader";
import useEvacuationManger from "../../../../../hooks/useEvacuationManager";

const PAGE_SIZE = 10;
const SCROLL_LOAD_START_PAGE = 2;

type Props = {};

export default function EmergencyManagerSituationList(props: Props) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStatusLoading, setIsStatusLoading] = useState<boolean>(false);
  const [selectedSituation, setSelectedSituation] = useState<
    SituationItem | undefined
  >(undefined);
  const [selectedBuilding, setSelectedBuilding] = useState<string | undefined>(
    undefined
  ); // TODO : 건물 필터링 정책 결정 후 적용
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isStatusLoading,
  });

  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [popup, setPopup] = useState<"ready" | "evacuation" | "done" | "">("");

  const [isManager, isUser, { user }, isAdminOrMaster] = useEvacuationManger();

  const [statusOption, setStatusOption] = useState<SummaryButtonOption[]>([
    { status: "진행중", value: "IN_PROGRESS" },
    { status: "준비", value: "CREATED" },
    { status: "종료", value: "ENDED" },
    { status: "전체", value: "all" },
  ]);

  // TODO : buildingId 가 기본적으로 셋팅되도록
  const [searchParams, setSearchParams] = useState<
    Partial<SituationListSearchType>
  >({
    status: "IN_PROGRESS",
  });

  const [evacuationList, setEvacuationList] = useState<SituationItem[]>([]);

  const [selectedStatusOption, setSelectedStatusOption] =
    useState<SummaryButtonOption>(statusOption[0]);

  const handleRefresh = () => {
    loadSituation();
    loadSituationStatusCount();
  };

  const loadSituation = async (args?: InfiniteScrollCallbackArgs) => {
    let itemLength = INFINITE_CODE.ERROR;
    setIsLoading(true);
    try {
      const response = await getSituationList({ ...searchParams, ...args }); // TODO : 건물id 로 가져오도록 변경 필요
      if (response.status === HttpStatusCode.Ok) {
        if (!args?.page || args?.page === 1) {
          await setEvacuationList(response.data.items);
        } else {
          await setEvacuationList((prev) => [...prev, ...response.data.items]);
        }
        itemLength = response.data.items.length ?? 0;
      } else {
        open("상황 목록을 불러오는데 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("loadFailed");
      }
    } finally {
      setIsLoading(false);
    }

    return itemLength;
  };

  const loadSituationStatusCount = async () => {
    setIsStatusLoading(true);
    try {
      const response = await getSituationCount({}); // TODO : 건물id 로 가져오도록 변경 필요
      if (response.status === HttpStatusCode.Ok) {
        const countInfo = response.data;
        setStatusOption([
          {
            status: "진행중",
            value: "IN_PROGRESS",
            count: countInfo.totalInProgress,
          },
          { status: "준비", value: "CREATED", count: countInfo.totalCreated },
          { status: "종료", value: "ENDED", count: countInfo.totalEnded },
          { status: "전체", value: "all", count: countInfo.totalAll },
        ]);
      } else {
        open("상황별 통계를 불러오는데 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("loadSituationStatus Failed");
      }
    } finally {
      setIsStatusLoading(false);
    }
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading,
    callback: loadSituation,
    startPage: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    resetScroll();
    loadSituation({
      page: SCROLL_LOAD_START_PAGE - 1,
      pageSize: PAGE_SIZE,
    });
  }, [searchParams]);

  useEffect(() => {
    loadSituationStatusCount();
  }, [selectedBuilding, searchParams.status]);

  const handleStartSituation = (evacuationId: string) => {
    postStart(evacuationId);
    setPopup("");
    setSelectedSituation(undefined);
  };

  const handleEndSituation = (evacuationId: string) => {
    postEnd(evacuationId);
    setPopup("");
    setSelectedSituation(undefined);
  };

  const postStart = async (situationId: string) => {
    try {
      const res = await putSituationStart(situationId);
      if (res.status === HttpStatusCode.Ok) {
        open("상황이 발생 되었습니다.");
      } else {
        open("상황 시작이 실패하였습니다. 다시 시도해주세요.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("postStartFailed");
      }
    } finally {
      handleRefresh();
    }
  };

  const postEnd = async (situationId: string) => {
    try {
      const res = await putSituationEnd(situationId);
      if (res.status === HttpStatusCode.Ok) {
        open("상황이 종료 되었습니다.");
      } else {
        open("상황 종료가 실패하였습니다. 다시 시도해주세요.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("postEndFailed");
      }
    } finally {
      handleRefresh();
    }
  };

  return (
    <>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="비상대피 관리"
          rightContent={
            <div onClick={handleRefresh}>
              <RefreshIcon />
            </div>
          }
          onBackClick={() => {
            navigate(-1);
          }}
        />
      </div>

      <div className="p-2 flex flex-col gap-2.5 text-3xl font-light">
        {/* TODO : 정책 확인 필요 - 건물목록 표시 */}
      </div>
      <div className="bg-brand-weather-badge-background py-2.5 px-5 grid grid-cols-4 gap-x-1">
        {statusOption.map((option, index) => (
          <SummaryButton
            key={index}
            {...option}
            onClick={() => {
              setSelectedStatusOption(option);
              if (option.value === "all") {
                setSearchParams((prev) => ({
                  ...prev,
                  status: undefined,
                }));
              } else {
                setSearchParams((prev) => ({
                  ...prev,
                  status: option.value as SituationStatusType,
                }));
              }
            }}
            active={selectedStatusOption.status === option.status}
          />
        ))}
      </div>
      <div className="px-5 pt-[30px] flex flex-col gap-2.5 mb-2">
        <div className=" text-lg font-medium">상황 목록</div>
      </div>
      {/* TODO - check : (24/08/25) 요구사항에 맞는 멘트를 향후 커스텀 가능 하도록 변경 예정 */}
      {/* <div
        className="mx-5 mt-2 mb-3 px-2 py-1 bg-brand-neutral-05 text-xs whitespace-normal rounded-[810px]"
        style={{ wordBreak: "keep-all" }}
      >
        시작 후, 무용답자에게는 10분마다 대피요령 알림이 발송됩니다.
      </div> */}
      <div className="border-b mx-5 border-brand-neutral-100" />

      <div className="mx-5 mt-3 flex flex-col gap-3">
        {evacuationList.map((item, index) => {
          return (
            <div
              className="flex flex-row justify-between rounded-[10px] py-3 px-4 shadow-md"
              key={index}
            >
              {/* 왼쪽 부분 */}
              <div className="flex flex-col flex-1">
                {item.status === "CREATED" ? (
                  <></>
                ) : item.status === "IN_PROGRESS" ? (
                  <div className=" rounded-full bg-brand-cleaning-badge px-2 py-2.5 h-5 flex items-center text-[12px] font-medium leading-4 text-slate-100 w-fit">
                    진행중
                  </div>
                ) : (
                  <div className=" rounded-full bg-brand-gray-40-opacity px-2 py-2.5 h-5 flex items-center text-[12px] font-medium leading-4 text-slate-100 w-fit">
                    상황 종료
                  </div>
                )}
                <div className="font-bold text-md mt-1">
                  {item.evacuationCategoryName}
                </div>
                <div className="text-md font-medium">{item.evacuationName}</div>

                <div className="flex flex-row items-center text-xs text-brand-neutral-50 font-normal mt-2">
                  <MdLocationPin />
                  <div className="ml-1 break-words">{item.incidentAddress}</div>
                </div>
                <div className="flex flex-row items-center text-xs text-brand-neutral-50 font-normal mb-3">
                  <MdAccessTimeFilled />
                  <div className="ml-1">{item.createdAt}</div>
                </div>
                <div className="flex flex-row bg-brand-neutral-05 py-1 px-2 gap-1 justify-between flex-wrap rounded-[8px]">
                  <div className="flex flex-col flex-0">
                    <div className="flex justify-center text-base font-medium leading-[26px]">
                      {item.countByStatus?.totalCount ?? 0}
                    </div>
                    <div className="flex justify-center text-brand-neutral-60 text-xs leading-4 break-words">
                      총인원
                    </div>
                  </div>
                  <div className="flex flex-col flex-0">
                    <div className="flex justify-center text-base font-medium leading-[26px]">
                      {item.countByStatus?.createNoResponseCount ?? 0}
                    </div>
                    <div className="flex justify-center text-brand-neutral-60 text-xs leading-4 break-words">
                      무응답
                    </div>
                  </div>
                  <div className="flex flex-col flex-0">
                    <div className="flex justify-center text-base font-medium leading-[26px]">
                      {item.countByStatus?.evacuationCompleteCount ?? 0}
                    </div>
                    <div className="flex justify-center text-brand-neutral-60 text-xs leading-4 break-words">
                      대피완료
                    </div>
                  </div>
                  <div className="flex flex-col flex-0">
                    <div className="flex justify-center text-base font-medium leading-[26px]">
                      {item.countByStatus?.onLeaveOrOUtCount ?? 0}
                    </div>
                    <div className="flex justify-center text-brand-neutral-60 text-xs leading-4 break-words">
                      휴가/외근
                    </div>
                  </div>
                  <div className="flex flex-col flex-0">
                    <div className="flex justify-center text-base font-medium leading-[26px] text-red-600">
                      {item.countByStatus?.helpMeCount ?? 0}
                    </div>
                    <div className="flex justify-center text-brand-neutral-60 text-xs leading-4 break-words">
                      구조요청
                    </div>
                  </div>
                </div>
              </div>

              {/* 오른쪽 두 버튼 영역*/}
              <div className="flex flex-col min-h-full gap-1.5 pl-2 ml-2 border-l border-brand-neutral-05">
                {isAdminOrMaster &&
                  (item.status === "IN_PROGRESS" ? (
                    <div
                      className="flex items-center justify-center rounded-[8px] p-2 bg-brand-primary-magenta gap-0.5 text-slate-50 text-sm flex-1"
                      onClick={() => {
                        setPopup("done");
                        setSelectedSituation(item);
                      }}
                    >
                      상황
                      <br />
                      종료
                    </div>
                  ) : item.status === "CREATED" ? (
                    <div
                      className="flex items-center justify-center rounded-[8px] p-2 bg-brand-primary-magenta gap-0.5  text-slate-50 text-sm flex-1"
                      onClick={() => {
                        setPopup("ready");
                        setSelectedSituation(item);
                      }}
                    >
                      상황
                      <br />
                      시작
                    </div>
                  ) : (
                    <div className="flex items-center justify-center rounded-[8px] p-2 bg-brand-primary-gray-100 gap-0.5  text-slate-50 text-sm flex-1">
                      상황
                      <br />
                      종료
                    </div>
                  ))}
                <div
                  className="flex items-center justify-center rounded-[8px] p-2 bg-white gap-0.5 text-black bg-brand-gray-10-opacity flex-1 text-sm"
                  onClick={() =>
                    navigate(`/manager/evacuation-manager/${item.evacuationId}`)
                  }
                >
                  상세
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CommonPopup
        isOpen={popup === "ready"}
        onClose={() => {
          setPopup("");
          setSelectedSituation(undefined);
        }}
        padding={0}
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <div className="flex flex-col mt-4">
              <div className="flex items-center justify-center font-bold">
                [{selectedSituation?.evacuationCategoryName ?? ""}]
              </div>
              <div className="flex items-center justify-center font-bold mb-3">
                {selectedSituation?.evacuationGroupName ?? ""}
              </div>
              <div className="flex items-center justify-center font-medium text-sm">
                상황을 시작하겠습니까?
              </div>
            </div>
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => {
                setPopup("");
                setSelectedSituation(undefined);
              }}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={() =>
                handleStartSituation(selectedSituation?.evacuationId ?? "")
              }
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <CommonPopup
        isOpen={popup === "done"}
        onClose={() => {
          setPopup("");
          setSelectedSituation(undefined);
        }}
        padding={0}
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <div className="flex flex-col mt-4">
              <div className="flex items-center justify-center font-bold">
                [{selectedSituation?.evacuationCategoryName ?? ""}]
              </div>
              <div className="flex items-center justify-center font-bold mb-3">
                {selectedSituation?.evacuationGroupName ?? ""}
              </div>
              <div className="flex items-center justify-center font-medium text-sm">
                상황을 종료하시겠습니까?
              </div>
              <div className="flex items-center justify-center font-medium text-sm">
                (무응답:{" "}
                {selectedSituation?.countByStatus.createNoResponseCount}명)
              </div>
            </div>
          </div>

          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => {
                setPopup("");
                setSelectedSituation(undefined);
              }}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={() =>
                handleEndSituation(selectedSituation?.evacuationId ?? "")
              }
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      {simplePopupComponent}
    </>
  );
}
